import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { videoService, type VideoWithProvider } from '../../services'
import { useAuthStore } from '../../stores'
import { VideoPlayer } from '../../components/video'
import { CommentSection } from '../../components/comment'
import { Button, LoadingScreen } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { getViewSessionId } from '../../utils/viewSession'

export function VideoDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { profile, isAuthenticated } = useAuthStore()
  const toast = useToast()

  const [video, setVideo] = useState<VideoWithProvider | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [viewCount, setViewCount] = useState(0)

  // Track if view has been logged for this video session
  const viewLoggedRef = useRef(false)

  useEffect(() => {
    const loadVideo = async () => {
      if (!id) return

      setIsLoading(true)
      try {
        const videoData = await videoService.getVideoById(id)
        setVideo(videoData)
        setLikeCount(videoData?.like_count || 0)
        setViewCount(videoData?.view_count || 0)

        // Check if user has liked
        if (profile) {
          const liked = await videoService.hasLiked(id, profile.id)
          setIsLiked(liked)
        }
      } catch (error) {
        console.error('Failed to load video:', error)
        toast.error('Failed to load video')
      } finally {
        setIsLoading(false)
      }
    }

    loadVideo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile])

  const handleLike = async () => {
    if (!isAuthenticated() || !profile || !id) {
      toast.info('Please sign in to like videos')
      return
    }

    try {
      if (isLiked) {
        await videoService.unlikeVideo(id, profile.id)
        setIsLiked(false)
        setLikeCount((c) => c - 1)
      } else {
        await videoService.likeVideo(id, profile.id)
        setIsLiked(true)
        setLikeCount((c) => c + 1)
      }
    } catch (error) {
      console.error('Failed to toggle like:', error)
      toast.error('Failed to update like')
    }
  }

  // Reset view logged flag when video changes
  useEffect(() => {
    viewLoggedRef.current = false
  }, [id])

  const handleVideoProgress = useCallback(
    async (currentTime: number, duration: number) => {
      // Skip if already logged or no video
      if (viewLoggedRef.current || !id) return

      // Calculate progress percentage
      const progress = duration > 0 ? currentTime / duration : 0

      // Log view after 3 seconds OR 50% watched (whichever comes first)
      // This handles both short videos (like 3-sec clips) and longer ones
      const shouldLogView = currentTime >= 3 || progress >= 0.5

      if (shouldLogView) {
        viewLoggedRef.current = true
        const sessionId = getViewSessionId()
        const completed = progress >= 0.9

        await videoService.logView(
          id,
          profile?.id || null,
          sessionId,
          Math.floor(currentTime),
          completed
        )

        // Update local view count immediately for responsive UI
        setViewCount((c) => c + 1)
      }
    },
    [id, profile]
  )

  if (isLoading) {
    return <LoadingScreen message="Loading video..." />
  }

  if (!video) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Video Not Found</h1>
        <p className="mt-2 text-gray-600">
          The video you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/videos" className="mt-4 inline-block">
          <Button>Browse Videos</Button>
        </Link>
      </div>
    )
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2">
          {/* Video Player */}
          <VideoPlayer video={video} onProgress={handleVideoProgress} />

          {/* Video info */}
          <div className="mt-6">
            <h1 className="text-2xl font-bold text-gray-900">{video.title}</h1>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>{viewCount} {viewCount === 1 ? 'view' : 'views'}</span>
                <span>·</span>
                <span>{formatDuration(video.duration_seconds)}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isLiked
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill={isLiked ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  {likeCount}
                </button>

                <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  Share
                </button>
              </div>
            </div>

            {/* Description */}
            {video.description && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {video.description}
                </p>
              </div>
            )}

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/videos?tags=${tag}`}
                    className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Comments Section */}
            <div className="mt-8">
              <CommentSection videoId={video.id} videoOwnerId={video.user_id} />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Provider card */}
          {video.user && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {video.user.avatar_url ? (
                    <img
                      src={video.user.avatar_url}
                      alt={video.user.full_name || 'Provider'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-medium text-gray-600">
                      {video.user.full_name?.[0]?.toUpperCase() || 'P'}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {video.user.full_name || 'Unknown Provider'}
                  </h3>
                  {video.user.specialty && (
                    <p className="text-sm text-gray-600">
                      {video.user.specialty}
                    </p>
                  )}
                </div>
              </div>

              {video.user.bio && (
                <p className="mt-4 text-sm text-gray-600 line-clamp-3">
                  {video.user.bio}
                </p>
              )}

              <div className="mt-4 space-y-2">
                <Link to={`/providers/${video.user.id}`}>
                  <Button fullWidth variant="outline">
                    View Profile
                  </Button>
                </Link>
                <Button fullWidth>Contact Provider</Button>
              </div>
            </div>
          )}

          {/* Related info */}
          <div className="mt-6 bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              Video Information
            </h3>
            <dl className="space-y-3 text-sm">
              {video.category && (
                <div className="flex justify-between">
                  <dt className="text-gray-600">Category</dt>
                  <dd className="font-medium">{video.category}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-600">Duration</dt>
                <dd className="font-medium">
                  {formatDuration(video.duration_seconds)}
                </dd>
              </div>
              {video.resolution && (
                <div className="flex justify-between">
                  <dt className="text-gray-600">Quality</dt>
                  <dd className="font-medium">{video.resolution}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-600">Uploaded</dt>
                <dd className="font-medium">
                  {new Date(video.created_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
